import React, { useEffect, useState } from 'react';
import { Card, Modal, Button } from 'react-bootstrap';
import { Cnt } from '../Cnt.ThreeDeeCrate.SDK/Cnt.ThreeDeeCrate.SDK';
import { EMPTY_GUID } from '../types/Guid';
import { useNavigate } from 'react-router-dom';
import { AppSettings } from '../AppSettings';

import './ModelCard.scss';
import { ModelModalContent } from './ModelModalContent';
import { ThumbnailProgress } from './ProgressBars/ThumbnailProgress';
import { useUser } from '../context/UserProvider';

interface Props {
  node: Cnt.ThreeDeeCrate.SDK.ContainerDTO;
  currentPath: string[];                 // path segments (no style, no encoding)
  autoOpen?: boolean;
  onEdit: (model: Cnt.ThreeDeeCrate.SDK.ModelDTO | undefined) => void;
  styleName: string;                     // ✅ e.g. "Stylized" or "Standard"
}

export const ModelCard: React.FC<Props> = ({ node, currentPath, autoOpen, onEdit, styleName }) => {
  const [status, setStatus] = useState<Cnt.ThreeDeeCrate.SDK.ModelStatusType>(Cnt.ThreeDeeCrate.SDK.ModelStatusType.IsNotLoggedIn);
  const [showModal, setShowModal] = useState(false);
  const [model, setModel] = useState<Cnt.ThreeDeeCrate.SDK.ContainerModelDTO | null>(null);

  const navigate = useNavigate();

  const buildCategoryPath = (segments: string[]) =>
    segments.map(s => encodeURIComponent(s)).join('--');

  const goToCategoryPath = (segments: string[], replace = false) => {
    const encodedCategory = buildCategoryPath(segments);
    // ✅ Always keep style segment
    navigate(`/${encodeURIComponent(styleName)}/${encodedCategory}`, { replace });
  };

  useEffect(() => {
    const api = new Cnt.ThreeDeeCrate.SDK.Api(AppSettings.APIUrl);
    const fetchFull = async () => {
      const m = await api.model.GetModelInformation(node.Id ?? EMPTY_GUID);
      setModel(m);
    };
    fetchFull();
  }, [node.Id]);

  useEffect(() => {
    if (!showModal) return;
    const fetchStatus = async () => {
      const api = new Cnt.ThreeDeeCrate.SDK.Api(AppSettings.APIUrl);
      try {
        const modelStatus = await api.model.CheckUserModelStatus({
          Url: window.location.href,
          ContainerId: node.Id || EMPTY_GUID,
        });
        setStatus(modelStatus ?? Cnt.ThreeDeeCrate.SDK.ModelStatusType.IsNotLoggedIn);
      } catch (error) {
        console.error('❌ Error refreshing model status', error);
      }
    };
    fetchStatus();
  }, [showModal, node.Id]);

  useEffect(() => {
    if (autoOpen) {
      goToCategoryPath([...currentPath, node.Name ?? ''], true);
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen, node.Name]); // currentPath is stable per render

  const handleOpen = () => {
    if (!showModal) {
      goToCategoryPath([...currentPath, node.Name ?? ''], false);
      setShowModal(true);
    }
  };

  const handleClose = () => setShowModal(false);
  const { user } = useUser();
  const isAdmin = user?.IsAdmin === true;

  return (
    <>
      <Card
        className={`h-100 w-100 shadow-sm hover-card ${node.Visibility === Cnt.ThreeDeeCrate.SDK.VisibilityType.Private ? 'border-danger text-danger' : ''}`}
        style={{ cursor: 'pointer', position: 'relative' }}
        onClick={handleOpen}
      >
        <div className="card-thumbnail-wrapper">
          <ThumbnailProgress
            containerId={model?.Container?.Id ?? EMPTY_GUID}
            status={model?.Model?.GenerationStatus ?? Cnt.ThreeDeeCrate.SDK.GenerationStatusType.Done}
            smallThumbnail={true}
            isRecommended={model?.Model?.IsRecommended ?? false}
          />
        </div>
        <Card.Body className="d-flex flex-column justify-content-between">
          <div>
            <Card.Title>{node.Name}</Card.Title>
            {node.Description && (
              <Card.Text style={{ fontSize: '0.9rem', color: '#ccc' }}>
                {node.Description.length > 80 ? node.Description.substring(0, 80) + '…' : node.Description}
              </Card.Text>
            )}
          </div>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{node.Name}</Modal.Title>
        </Modal.Header>
        <Modal.Body><ModelModalContent model={model} /></Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>Close</Button>
          {isAdmin && (
            <Button variant="primary" onClick={() => { handleClose(); onEdit(model?.Model ?? undefined); }}>
              Edit
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
